'use es6';

import I18n from 'I18n';
import { TabValidationsKeys, ValidationKeys, ValidationType } from 'FormsUI/constants/FormFieldValidations';
import { FORM } from 'FormsUI/constants/FormPreviewSteps';
import { FieldTypes } from 'ui-forms-components/constants/FieldTypeMapping';
import { PAYMENT_FIELD_IDENTIFIER } from 'FormsUI/constants/Payments';
import { getPaymentSessionTemplates, getPaymentEligibility, getCheckoutSessionPreview } from 'FormsUI/api/Payments';
import { getCheckoutSessionPreviewRequestState, getIsPaymentsAvailable, getPaymentEligibilityRequestState, getPaymentsDataRequestState, getActivePaymentSessionTemplateId } from 'FormsUI/selectors/Payments';
import { getPaymentFieldFromFormDefinition, getFormLanguage } from 'FormsUI/selectors/FormDefinition';
import { addValidationErrorIfNeeded, removeValidationErrorIfNeeded, revertUpdatePaymentField, updateFormValue } from 'FormsUI/actions/FormDefinition';
import { setPagePreviewStep } from 'FormsUI/actions/FormPreview';
import { appendFormFieldGroup } from 'FormsUI/actions/FormDragDrop';
import { getFormsLangToLang } from 'FormsUI/utils/Lang';
const createAction = name => `@@payments/${name}`;
export const REQUEST_PAYMENTS = createAction('REQUEST_PAYMENTS');
export const RECEIVE_PAYMENTS = createAction('RECEIVE_NOTIFICATION_PREFERENCES');
export const SUCCESS_GETTING_PAYMENTS = createAction('SUCCESS_GETTING_PAYMENTS');
export const FAILED_GETTING_PAYMENTS = createAction('FAILED_GETTING_PAYMENTS');
export const REQUEST_PAYMENT_ELIGIBILITY = createAction('REQUEST_PAYMENT_ELIGIBILITY');
export const RECEIVE_PAYMENT_ELIGIBILITY = createAction('RECEIVE_PAYMENT_ELIGIBILITY');
export const FAILED_GETTING_PAYMENT_ELIGIBILITY = createAction('FAILED_GETTING_PAYMENT_ELIGIBILITY');
export const UPDATE_TOGGLE_STATE = createAction('UPDATE_TOGGLE_STATE');
export const REQUEST_CHECKOUT_SESSION_PREVIEW = createAction('REQUEST_CHECKOUT_SESSION_PREVIEW');
export const RECEIVE_CHECKOUT_SESSION_PREVIEW = createAction('RECEIVE_CHECKOUT_SESSION_PREVIEW');
export const SUCCESS_GETTING_CHECKOUT_SESSION_PREVIEW = createAction('SUCCESS_GETTING_CHECKOUT_SESSION_PREVIEW');
export const FAILED_GETTING_CHECKOUT_SESSION_PREVIEW = createAction('FAILED_GETTING_CHECKOUT_SESSION_PREVIEW');
const requestPayments = () => ({
  type: REQUEST_PAYMENTS
});
const receivePayments = payload => ({
  type: RECEIVE_PAYMENTS,
  payload
});
const successGettingPayments = () => ({
  type: SUCCESS_GETTING_PAYMENTS
});
const failedGettingPayments = () => ({
  type: FAILED_GETTING_PAYMENTS
});
const requestPaymentEligibility = () => ({
  type: REQUEST_PAYMENT_ELIGIBILITY
});
const receivePaymentEligibility = payload => ({
  type: RECEIVE_PAYMENT_ELIGIBILITY,
  payload
});
const failedGettingPaymentEligibility = () => ({
  type: FAILED_GETTING_PAYMENT_ELIGIBILITY
});
const requestCheckoutSessionPreview = paymentSessionTemplateId => ({
  type: REQUEST_CHECKOUT_SESSION_PREVIEW,
  paymentSessionTemplateId
});
const receiveCheckoutSessionPreview = (paymentSessionTemplateId, payload) => ({
  type: RECEIVE_CHECKOUT_SESSION_PREVIEW,
  paymentSessionTemplateId,
  payload
});
const successGettingCheckoutSessionPreview = paymentSessionTemplateId => ({
  type: SUCCESS_GETTING_CHECKOUT_SESSION_PREVIEW,
  paymentSessionTemplateId
});
const failedGettingCheckoutSessionPreview = (paymentSessionTemplateId, error) => ({
  type: FAILED_GETTING_CHECKOUT_SESSION_PREVIEW,
  paymentSessionTemplateId,
  error
});
export const updateToggleState = toggleState => ({
  type: UPDATE_TOGGLE_STATE,
  toggleState
});
export const fetchPayments = () => (dispatch, getState) => {
  const state = getState();
  const paymentsRequestState = getPaymentsDataRequestState(state);
  if (!paymentsRequestState.shouldBeRequested() || !getIsPaymentsAvailable(state)) {
    return Promise.resolve();
  }
  dispatch(requestPayments());
  return getPaymentSessionTemplates().then(payments => {
    dispatch(successGettingPayments());
    dispatch(receivePayments(payments));
  }).catch(error => {
    dispatch(failedGettingPayments(error));
  });
};
export const removeValidationErrorsForPayments = () => dispatch => {
  dispatch(removeValidationErrorIfNeeded(TabValidationsKeys.OPTIONS, ValidationKeys.REDIRECT_PAGE, ValidationType.REQUIRED));
  dispatch(removeValidationErrorIfNeeded(TabValidationsKeys.OPTIONS, ValidationKeys.REDIRECT_PAGE, ValidationType.INVALID));
  dispatch(removeValidationErrorIfNeeded(TabValidationsKeys.OPTIONS, ValidationKeys.THANK_YOU_MESSAGE, ValidationType.REQUIRED));
  dispatch(removeValidationErrorIfNeeded(TabValidationsKeys.FORM, ValidationKeys.PAYMENT_LINK, ValidationType.REQUIRED));
};
export const removeOnlyPaymentValidationError = () => dispatch => {
  dispatch(removeValidationErrorIfNeeded(TabValidationsKeys.FORM, ValidationKeys.PAYMENT_LINK, ValidationType.REQUIRED));
};
export const addPaymentsValidationError = () => dispatch => {
  dispatch(addValidationErrorIfNeeded(TabValidationsKeys.FORM, ValidationKeys.PAYMENT_LINK, ValidationType.REQUIRED));
};
export const fetchPaymentEligibility = () => (dispatch, getState) => {
  const state = getState();
  const paymentEligibilityRequestState = getPaymentEligibilityRequestState(state);
  if (!paymentEligibilityRequestState.shouldBeRequested()) {
    return Promise.resolve();
  }
  dispatch(requestPaymentEligibility());
  return getPaymentEligibility().then(payments => {
    dispatch(receivePaymentEligibility(payments));
  }).catch(error => {
    dispatch(failedGettingPaymentEligibility(error));
  });
};
export const fetchCheckoutSessionPreview = () => (dispatch, getState) => {
  const state = getState();
  const paymentSessionTemplateId = getActivePaymentSessionTemplateId(state);
  const checkoutSessionPreviewRequestState = getCheckoutSessionPreviewRequestState(state);
  if (!paymentSessionTemplateId || !checkoutSessionPreviewRequestState.shouldBeRequested()) {
    return Promise.resolve();
  }
  dispatch(requestCheckoutSessionPreview(paymentSessionTemplateId));
  return getCheckoutSessionPreview(paymentSessionTemplateId).then(checkoutSessionPreview => {
    dispatch(successGettingCheckoutSessionPreview(paymentSessionTemplateId));
    dispatch(receiveCheckoutSessionPreview(paymentSessionTemplateId, checkoutSessionPreview));
  }).catch(error => {
    dispatch(failedGettingCheckoutSessionPreview(paymentSessionTemplateId, error));
  });
};
export const enablePayments = () => (dispatch, getState) => {
  const state = getState();
  const formLanguage = getFormLanguage(state);
  dispatch(appendFormFieldGroup(PAYMENT_FIELD_IDENTIFIER, {
    newFieldType: FieldTypes.PAYMENT_RADIO,
    showConfigPanel: false
  }));
  dispatch(updateFormValue({
    submitText: I18n.text('defaultForm.continueToPayment', {
      locale: getFormsLangToLang(formLanguage)
    })
  }));
  dispatch(addPaymentsValidationError());
  dispatch(updateToggleState(true));
};
export const disablePayments = () => (dispatch, getState) => {
  const handleAfterRemovedPaymentsFromForm = () => {
    dispatch(updateToggleState(false));
    dispatch(setPagePreviewStep(FORM));
    dispatch(removeOnlyPaymentValidationError());
  };
  dispatch(revertUpdatePaymentField(null));
  if (!getPaymentFieldFromFormDefinition(getState())) {
    handleAfterRemovedPaymentsFromForm();
  }
};